// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Contract-duplicates tab — groups findings by category (conflict, true
 * duplicate, flow-down, cleanup) and sorts each group by severity.
 */
import { type FC, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Copy } from 'lucide-react'
import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui'

import type {
  ContractDuplicateCategory,
  ContractDuplicateFinding,
  ContractDuplicateSeverity,
} from '../../hooks/extractors'
import { EmptyState } from './shared'

export const DUPLICATE_CATEGORY_LABEL: Record<ContractDuplicateCategory, string> = {
  TRUE_DUPLICATE: 'Duplicate',
  CONFLICT_OVERRIDE: 'Conflict / Override',
  FLOW_DOWN_INCONSISTENCY: 'Flow-down Inconsistency',
  CLEANUP: 'Cleanup',
}

const DUPLICATE_CATEGORY_DESCRIPTION: Record<ContractDuplicateCategory, string> = {
  TRUE_DUPLICATE: 'Same obligation imposed in two places — delete or merge.',
  CONFLICT_OVERRIDE: 'Two provisions overlap; one may silently narrow the other.',
  FLOW_DOWN_INCONSISTENCY: 'Body and exhibit/schedule restate the same term differently.',
  CLEANUP: 'Tracked-change or numbering artifacts to clean up.',
}

const DUPLICATE_CATEGORY_ORDER: ContractDuplicateCategory[] = [
  'CONFLICT_OVERRIDE',
  'TRUE_DUPLICATE',
  'FLOW_DOWN_INCONSISTENCY',
  'CLEANUP',
]

const SEVERITY_RANK: Record<ContractDuplicateSeverity, number> = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
}

const SEVERITY_BADGE_CLASS: Record<ContractDuplicateSeverity, string> = {
  HIGH: 'bg-red-100 text-red-800 border-red-200',
  MEDIUM: 'bg-amber-100 text-amber-800 border-amber-200',
  LOW: 'bg-muted text-muted-foreground border-muted-foreground/20',
}

export const DuplicatesTab: FC<{ duplicates: ContractDuplicateFinding[] }> = ({ duplicates }) => {
  const grouped = useMemo(() => {
    const map = new Map<ContractDuplicateCategory, ContractDuplicateFinding[]>()
    for (const d of duplicates) {
      const list = map.get(d.category) ?? []
      list.push(d)
      map.set(d.category, list)
    }
    for (const list of map.values()) {
      list.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity])
    }
    return DUPLICATE_CATEGORY_ORDER
      .map(category => ({ category, items: map.get(category) ?? [] }))
      .filter(group => group.items.length > 0)
  }, [duplicates])

  if (!duplicates.length) {
    return <EmptyState message="No duplicate, conflicting, or flow-down inconsistencies identified." />
  }

  return (
    <div
      className="space-y-3"
      role="list"
      aria-label="Contract duplicates"
      data-testid="contract-duplicates"
    >
      {grouped.map(({ category, items }) => (
        <DuplicateCategorySection key={category} category={category} items={items} />
      ))}
    </div>
  )
}

const DuplicateCategorySection: FC<{
  category: ContractDuplicateCategory
  items: ContractDuplicateFinding[]
}> = ({ category, items }) => {
  const [open, setOpen] = useState(true)
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="border rounded"
      data-testid="contract-duplicate-category"
    >
      <CollapsibleTrigger asChild>
        <div
          className="flex items-center gap-2 py-2 px-3 cursor-pointer hover:bg-muted/50 transition-colors"
          role="button"
          aria-expanded={open}
          tabIndex={0}
        >
          <Copy className="h-4 w-4 text-indigo-500" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">{DUPLICATE_CATEGORY_LABEL[category]}</div>
            <p className="text-xs text-muted-foreground mt-0.5">{DUPLICATE_CATEGORY_DESCRIPTION[category]}</p>
          </div>
          <Badge variant="outline" className="text-xs px-2 py-0">{items.length}</Badge>
          {open
            ? <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="divide-y border-t" role="list" aria-label={`${DUPLICATE_CATEGORY_LABEL[category]} findings`}>
          {items.map((d, i) => (
            <DuplicateFindingRow key={i} finding={d} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

const DuplicateFindingRow: FC<{ finding: ContractDuplicateFinding }> = ({ finding }) => {
  const [open, setOpen] = useState(false)
  const headline = finding.explanation.split(/(?<=\.)\s+/)[0] ?? finding.explanation
  const truncated = headline.length > 140 ? `${headline.slice(0, 137)}…` : headline

  return (
    <div role="listitem" data-testid="contract-duplicate-finding">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <div
            className="flex items-start gap-3 py-2 px-3 cursor-pointer hover:bg-muted/50 transition-colors"
            role="button"
            aria-expanded={open}
            tabIndex={0}
          >
            <Badge
              variant="outline"
              className={`text-xs px-2 py-0 mt-0.5 ${SEVERITY_BADGE_CLASS[finding.severity]}`}
            >
              {finding.severity}
            </Badge>
            <span className="text-sm flex-1">{truncated}</span>
            {finding.locations.slice(0, 2).map((loc, i) => (
              <Badge key={i} variant="secondary" className="font-mono text-xs px-2 py-0 mt-0.5">{loc}</Badge>
            ))}
            {finding.locations.length > 2 && (
              <span className="text-xs text-muted-foreground mt-0.5">+{finding.locations.length - 2}</span>
            )}
            {open
              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground mt-1" aria-hidden="true" />
              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground mt-1" aria-hidden="true" />}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="ml-6 mr-3 mb-2 space-y-2 border-l-2 border-muted pl-3 py-1.5">
            {finding.locations.length > 0 && (
              <div>
                <p className="text-xs font-medium text-foreground mb-1">Locations</p>
                <div className="space-y-1.5">
                  {finding.locations.map((loc, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Badge variant="secondary" className="font-mono text-xs px-2 py-0 shrink-0">{loc}</Badge>
                      {finding.excerpts[i] && (
                        <p className="text-sm text-foreground bg-muted/40 rounded px-2.5 py-1 leading-relaxed flex-1">
                          “{finding.excerpts[i]}”
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-foreground mb-0.5">Explanation</p>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{finding.explanation}</p>
            </div>
            {finding.consolidation && (
              <div>
                <p className="text-xs font-medium text-foreground mb-0.5">Consolidation</p>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{finding.consolidation}</p>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
